import { useEffect, useState, useMemo } from "react";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import Box from "@mui/material/Box";
import SubscriptionInformation from "./components/SubscriptionInformation";
import FeatureAddonsBillingSection from "./components/FeatureAddonsBillingSection";
import InvoicesTable from "./components/InvoicesTable";
import { startGetPayments } from "../../actions/payments";
import ModalInvoice from "./components/ModalInvoice";
import { getPathDatabaseByRole } from "../../actions/auth";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDefaultAppRoute } from "../../hooks/useIsAdvertisersDemo";
import { hasRoleCapability } from "../../utils/roleCapabilities";
import {
  ALERT_ICON_TYPE_SUCCESS,
  CAL_ONBOARDING_EN,
  CAL_ONBOARDING_ES,
} from "../../utils/constants";
import ContainerPage from "../../components/Containers/ContainerPage";
import {
  BILLING_AND_PAYMENTS_TITLE,
  BILLING_SPECIALIST_PURCHASE_SUCCESS_DESCRIPTION,
  BILLING_SPECIALIST_PURCHASE_SUCCESS_TITLE,
  BILLING_SPECIALIST_SCHEDULE_MEETING_BUTTON,
  MODAL_BUY_CREDITS_PURCHASED,
  MODAL_BUY_CREDITS_PURCHASED_DESCRIPTION,
  MODAL_BUY_VIDEOS_PURCHASED,
  MODAL_BUY_VIDEOS_PURCHASED_DESCRIPTION,
} from "../../i18n/keysTranslations";
import TitlePage from "../../components/Texts/TitlePage";
import { useTranslationApp } from "../../lib/i18next";
import { ConfirmAlert, SimpleAlert } from "../../components/Alerts/Alerts";
import SuccessConfetti from "../../components/Common/SuccessConfetti";
import { openLink } from "../../utils/string";
import { pickLocalizedValue } from "../../utils/i18nLanguage";
import i18next from "i18next";
import { getLastPendingPackagePlanInvoiceEntry } from "../../utils/pendingPackagePlanInvoice";
import { usePendingSubscriptionPaymentAlert } from "./hooks/usePendingSubscriptionPaymentAlert";
import useSlideMenuCollapsed from "../../hooks/useSlideMenuCollapsed";

const BillingAndPayments = () => {
  const { t } = useTranslationApp();
  const [dataInvoices, setDataInvoices] = useState({});
  const [invoiceSelected, setInvoiceSelected] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [modalInvoiceOpen, setModalInvoiceOpen] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [params, setParams] = useSearchParams();

  const dispatch = useDispatchApp();
  const { drawerWidth, widthTransition } = useSlideMenuCollapsed();
  const { payments, isPlanActive } = useSelectorApp((state) => state.payments);
  const { userID, role } = dispatch(getPathDatabaseByRole());
  const navigate = useNavigate();
  const defaultAppRoute = useDefaultAppRoute();

  const getInitialData = () => {
    if (!hasRoleCapability(role, "canEditBilling")) {
      navigate(defaultAppRoute);
      return;
    }
    setIsLoading(true);
    setDataInvoices({});
    dispatch(
      startGetPayments({
        onCallbackFinish: () => setIsLoading(false),
      })
    );
  };

  useEffect(() => {
    getInitialData();
    // eslint-disable-next-line
  }, [userID]);

  useEffect(() => {
    if (showConfetti) {
      const timer = setTimeout(() => {
        setShowConfetti(false);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [showConfetti]);

  useEffect(() => {
    const creditsPurchase = params.get("creditsPurchase");
    const videosPurchase = params.get("videosPurchase");
    const specialistPurchase = params.get("specialistPurchase");

    if (creditsPurchase === "true") {
      SimpleAlert({
        title: t(MODAL_BUY_CREDITS_PURCHASED),
        text: t(MODAL_BUY_CREDITS_PURCHASED_DESCRIPTION),
        icon: ALERT_ICON_TYPE_SUCCESS,
      });
      setShowConfetti(true);
      params.delete("creditsPurchase");
      setParams(params);
    }

    if (videosPurchase === "true") {
      SimpleAlert({
        title: t(MODAL_BUY_VIDEOS_PURCHASED),
        text: t(MODAL_BUY_VIDEOS_PURCHASED_DESCRIPTION),
        icon: ALERT_ICON_TYPE_SUCCESS,
      });
      setShowConfetti(true);
      params.delete("videosPurchase");
      setParams(params);
    }

    if (specialistPurchase === "true" || specialistPurchase === "false") {
      const next = new URLSearchParams(params);
      next.delete("specialistPurchase");
      setParams(next, { replace: true });
    }

    if (specialistPurchase === "true") {
      (async () => {
        const result = await ConfirmAlert({
          title: t(BILLING_SPECIALIST_PURCHASE_SUCCESS_TITLE),
          text: t(BILLING_SPECIALIST_PURCHASE_SUCCESS_DESCRIPTION),
          icon: ALERT_ICON_TYPE_SUCCESS,
          showCancelButton: false,
          confirmButtonText: t(BILLING_SPECIALIST_SCHEDULE_MEETING_BUTTON),
        });
        if (result.isConfirmed) {
          openLink(
            pickLocalizedValue(i18next.language, {
              en: CAL_ONBOARDING_EN,
              es: CAL_ONBOARDING_ES,
            })
          );
        }
      })();
    }
    // eslint-disable-next-line
  }, [params]);

  useEffect(() => {
    if (isLoading) return;

    let filteredData = {};
    if (Object.keys(payments).length > 0) {
      Object.keys(payments).map((key) => {
        return (filteredData[key] = {
          ...payments[key],
          paidDate: payments[key].paidDate
            ? new Date(payments[key].paidDate)
            : "",
          dueDate: payments[key].dueDate ? new Date(payments[key].dueDate) : "",
        });
      });
    }

    setDataInvoices(filteredData);
  }, [payments, isLoading]);

  const pendingPackagePlanEntry = useMemo(() => {
    if (isLoading || isPlanActive) return null;
    return getLastPendingPackagePlanInvoiceEntry(payments);
  }, [payments, isPlanActive, isLoading]);

  usePendingSubscriptionPaymentAlert({
    invoice: pendingPackagePlanEntry?.invoiceData ?? null,
    invoiceKey: pendingPackagePlanEntry?.invoiceKey ?? null,
    enabled: !isLoading && !isPlanActive,
  });

  const onChangeStatusModalInvoice = (status, invoice = null) => {
    setModalInvoiceOpen(status);
    setInvoiceSelected(invoice);
  };

  return (
    <>
      <SuccessConfetti fire={showConfetti} />
      <ContainerPage
        mode="padding"
        hasMarginBottom={true}
        sx={{ px: 5, pt: 5 }}
      >
        <TitlePage>{t(BILLING_AND_PAYMENTS_TITLE)}</TitlePage>
        <Box
          sx={{
            mt: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            width: "100%",
            maxWidth: {
              xs: "100%",
              md: `calc(100vw - ${drawerWidth + 38}px)`,
            },
            transition: widthTransition("max-width"),
            "@media (prefers-reduced-motion: reduce)": { transition: "none" },
          }}
        >
          <Box
            sx={{
              width: "100%",
              mb: 3,
              display: "flex",
              gap: 2,
              flexDirection: {
                xs: "column",
                lg: "row",
              },
              justifyContent: "space-between",
            }}
          >
            <SubscriptionInformation />
          </Box>
          <FeatureAddonsBillingSection />
          <Box
            sx={{
              width: "100%",
            }}
          >
            <InvoicesTable
              data={dataInvoices}
              onSelectInvoice={(invoice) =>
                onChangeStatusModalInvoice(true, invoice)
              }
            />
          </Box>

          {modalInvoiceOpen && (
            <ModalInvoice
              data={dataInvoices[invoiceSelected]}
              invoice={invoiceSelected}
              modalOpen={modalInvoiceOpen}
              onCloseModal={() => onChangeStatusModalInvoice(false)}
            />
          )}
        </Box>
      </ContainerPage>
    </>
  );
};

export default BillingAndPayments;
